import React, { useMemo, useState } from "react";
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
  Avatar,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Alert,
  IconButton,
} from "@mui/material";
import {
  Logout,
  Person,
  Dashboard,
  School,
  Receipt,
  AccountBalance,
  Sync,
  Close,
  PermMedia,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { canAccessPaymentsPanel, canAccessContent, canAccessStudentInfo, canAccessSettings, canAccessPaymentsPortal, canSyncStudents, canAccessMedia } from "@/utils/permissions";
import { MEDIA_Feature, PAYMENTS_PANEL_Feature } from "@/features.config";
import StudentInfoModal from "@/pages/studentInfo/StudentInfoModal";
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import { getFunctions, httpsCallable } from "firebase/functions";
interface UserMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

interface MenuItemConfig {
  id: string;
  label: string;
  icon: React.ReactNode;
  path?: string;
  action?: () => void;
  checkPermission?: (userRole: any, email: any) => boolean;
  featureFlag?: boolean;
}


const UserMenu: React.FC<UserMenuProps> = ({ anchorEl, open, onClose }) => {
  const navigate = useNavigate();
  const { logout, authUser, userRole } = useAuth();
  console.log(authUser?.email);
  const [studentInfoModalOpen, setStudentInfoModalOpen] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncSuccess, setSyncSuccess] = useState(false);

  const handleSyncStudents = async () => {
    setSyncLoading(true);
    setSyncError(null);
    setSyncSuccess(false);

    try {
      const functions = getFunctions();
      const syncFunction = httpsCallable(functions, 'syncWorkspaceStudents');
      await syncFunction();
      setSyncSuccess(true);
    } catch (error: any) {
      console.error('Sync error:', error);
      setSyncError(
        error.message || 'Error al sincronizar estudiantes'
      );
    } finally {
      setSyncLoading(false);
    }
  };

  const handleCloseSyncDialog = () => {
    setSyncLoading(false);
    setSyncError(null);
    setSyncSuccess(false);
  };

  // Define menu items configuration with permissions
  const menuItems: MenuItemConfig[] = useMemo(() => [
    {
      id: 'profile',
      label: 'Mi Perfil',
      icon: <Person fontSize="small" sx={{ color: "primary.main" }} />,
      path: '/myprofile',
      featureFlag: true, // Always visible
    },
    {
      id: 'my-purchases',
      label: 'Mis Compras',
      icon: <Receipt fontSize="small" sx={{ color: "primary.main" }} />,
      path: '/mypurchases',
      checkPermission: canAccessPaymentsPortal,
      featureFlag: true,
    },
    {
      id: 'student-info',
      label: 'Consultar Estudiante',
      icon: <School fontSize="small" sx={{ color: "primary.main" }} />,
      action: () => setStudentInfoModalOpen(true),
      checkPermission: canAccessStudentInfo,
      featureFlag: true,
    },
    {
      id: 'content-admin',
      label: 'Administrar Contenido',
      icon: <Dashboard fontSize="small" sx={{ color: "primary.main" }} />,
      path: '/admin/content',
      checkPermission: canAccessContent,
      featureFlag: true,
    },
    {
      id: 'media-library',
      label: 'Biblioteca de Media',
      icon: <PermMedia fontSize="small" sx={{ color: "primary.main" }} />,
      path: '/admin/media',
      checkPermission: canAccessMedia,
      featureFlag: MEDIA_Feature === 'true',
    },
    {
      id: 'payments-panel',
      label: 'Tablero de Pagos',
      icon: <AccountBalance fontSize="small" sx={{ color: "primary.main" }} />,
      path: '/payments/panel',
      checkPermission: canAccessPaymentsPanel,
      featureFlag: PAYMENTS_PANEL_Feature && PAYMENTS_PANEL_Feature === 'true',
    },
    {
      id: 'settings',
      label: 'Ajustes',
      icon: <SettingsSuggestIcon fontSize="small" sx={{ color: "primary.main" }} />,
      path: '/admin/purchaseItem',
      checkPermission: canAccessSettings,
      featureFlag: true,
    },
    {
      id: 'sync-students',
      label: syncLoading ? 'Sincronizando...' : 'Sincronizar Estudiantes',
      icon: <Sync fontSize="small" sx={{ color: "primary.main" }} />,
      action: handleSyncStudents,
      checkPermission: canSyncStudents,
      featureFlag: true,
    },
  ], [syncLoading, handleSyncStudents]);

  // Filter menu items based on permissions and feature flags
  const visibleMenuItems = useMemo(() => {
    return menuItems.filter(item => {
      // Check feature flag
      if (!item.featureFlag) return false;

      // Check permission if defined
      if (item.checkPermission && !item.checkPermission(userRole, authUser?.email)) {
        return false;
      }

      return true;
    });
  }, [menuItems, userRole]);

  const handleLogout = async () => {
    try {
      await logout();
      onClose();
      navigate("/");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleMenuItemClick = (item: MenuItemConfig) => {
    if (item.action) {
      item.action();
      onClose();
    } else if (item.path) {
      handleNavigate(item.path);
    }
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        PaperProps={{
        elevation: 3,
        sx: {
          overflow: "visible",
          filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.15))",
          mt: 1.5,
          minWidth: 280,
          "& .MuiAvatar-root": {
            width: 32,
            height: 32,
            ml: -0.5,
            mr: 1,
          },
          "&:before": {
            content: '""',
            display: "block",
            position: "absolute",
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: "background.paper",
            transform: "translateY(-50%) rotate(45deg)",
            zIndex: 0,
          },
        },
      }}
      transformOrigin={{ horizontal: "right", vertical: "top" }}
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
    >
      {/* User Info Section */}
      <Box sx={{ px: 2, py: 2, minWidth: 280 }}>
        <Stack direction="column" spacing={1} alignItems="center">
          <Avatar
            src={authUser?.photoURL || undefined}
            sx={{
              width: 80,
              height: 80,
              bgcolor: "secondary.main",
              color: "primary.dark",
              fontSize: "2rem",
            }}
          >
            <Person sx={{ fontSize: "2.5rem" }} />
          </Avatar>
          <Box sx={{ textAlign: "center", width: "100%" }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: "text.primary",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {authUser?.displayName || "Usuario"}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: "0.875rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {authUser?.email}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Divider />

      {/* Menu Items - Dynamically rendered based on permissions */}
      {visibleMenuItems.map((item) => (
        <MenuItem
          key={item.id}
          onClick={(e) => {
            e.stopPropagation();
            handleMenuItemClick(item);
          }}
          sx={{
            py: 1.5,
            "&:hover": {
              backgroundColor: "primary.100",
            },
          }}
        >
          <ListItemIcon>
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
          />
        </MenuItem>
      ))}

      <Divider />

      {/* Logout */}
      <MenuItem
        onClick={(e) => {
          e.stopPropagation();
          handleLogout();
        }}
        sx={{
          py: 1.5,
          "&:hover": {
            backgroundColor: "error.200",
          },
        }}
      >
        <ListItemIcon>
          <Logout fontSize="small" sx={{ color: "error.main" }} />
        </ListItemIcon>
        <ListItemText
          primary="Cerrar Sesión"
          primaryTypographyProps={{
            fontSize: "0.875rem",
            fontWeight: 500,
            color: "error.main",
          }}
        />
      </MenuItem>
    </Menu>

    {/* Student Info Modal - Outside Menu to prevent closing issues */}
    <StudentInfoModal
      open={studentInfoModalOpen}
      onClose={() => setStudentInfoModalOpen(false)}
    />

    {/* Sync Students Dialog */}
    <Dialog
      open={syncLoading || syncError !== null || syncSuccess}
      onClose={syncLoading ? undefined : handleCloseSyncDialog}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Sincronización de Estudiantes</Typography>
          {!syncLoading && (
            <IconButton
              onClick={handleCloseSyncDialog}
              size="small"
              aria-label="cerrar"
            >
              <Close />
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box py={2}>
          {syncLoading && (
            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
              <CircularProgress />
              <Typography variant="body1">
                Sincronizando estudiantes desde Google Workspace...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Este proceso puede tardar varios minutos
              </Typography>
            </Box>
          )}

          {syncError && (
            <Alert severity="error">
              {syncError}
            </Alert>
          )}

          {syncSuccess && (
            <Alert severity="success">
              Sincronización completada exitosamente
            </Alert>
          )}
        </Box>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default UserMenu;
